import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import type { ValidatorFn } from '@angular/forms';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import moment from 'moment-timezone';

import { timeValidator as localizedTimeValidator } from '@recruitee/time';
import {
  ConnectedOverlayDirective,
  InputComponent,
  OverlayOriginDirective,
  TimePickerDropdownComponent,
} from '@recruitee/user-interface';

import type { DynamicTimePickerInputConfig } from '../../types';
import { DynamicFormInput } from '../dynamic-form/dynamic-form-input/dynamic-form-input';

const timeValidator = (control: UntypedFormControl): { time: true } | null =>
  !control.value || moment(control.value, 'HH:mm', true).isValid() ? null : { time: true };

export class TimePickerInput extends DynamicFormInput<DynamicTimePickerInputConfig> {
  protected validators(): ValidatorFn[] {
    return super.validators().concat([timeValidator]);
  }
}

@Component({
  selector: 'rti-time-picker-input',
  templateUrl: './time-picker-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ConnectedOverlayDirective,
    InputComponent,
    OverlayOriginDirective,
    ReactiveFormsModule,
    TimePickerDropdownComponent,
  ],
})
export class TimePickerInputComponent {
  @Input() public input: TimePickerInput;

  // Using an internal control, as the visible value can be different than the
  // underlying one. `input.control` always is for example `13:54`, but the
  // visible value is localized and could be `01:54 PM`.
  public internalControl: UntypedFormControl;

  public ngOnInit(): void {
    const initialInternalControlValue = this.input.control.value
      ? moment(this.input.control.value, 'HH:mm').format('LT')
      : '';

    this.internalControl = new UntypedFormControl(initialInternalControlValue, [
      localizedTimeValidator,
    ]);
    this.internalControl.valueChanges.subscribe(value => {
      const newValue = moment(value, 'LT', true);
      if (newValue.isValid()) {
        this.input.control.setValue(newValue.format('HH:mm'));
      } else {
        this.input.control.setValue({});
      }
    });
  }

  public valueChange(time: string): void {
    this.internalControl.setValue(time);
  }

  public getDisplayValue(): string {
    return this.input.control.value ? moment(this.input.control.value, 'HH:mm').format('LT') : '';
  }
}
